import { ExternalLink } from "lucide-mui";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { keys } from "../api/queryKeys.ts";
import { api } from "../api/client.ts";
import CountryName from "../components/CountryName";
import DetailRow from "../components/DetailRow.tsx";
import FormattedDate from "../components/FormattedDate";
import SectionCard from "../components/SectionCard.tsx";
import DescriptionBlock from "../components/detail/DescriptionBlock.tsx";
import DetailHeader from "../components/detail/DetailHeader.tsx";
import DetailPageStatus from "../components/detail/DetailPageStatus.tsx";
import MatchedCompanyCard from "../components/detail/MatchedCompanyCard.tsx";
import SourceDataCard from "../components/detail/SourceDataCard.tsx";
import { ensureHttps } from "../utils/url.ts";

const BACK = { to: "/job-ads", label: "Job ads" };

function JobAdDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: jobAd, isLoading, isError } = useQuery({
    queryKey: keys.jobAd.detail(id),
    queryFn: () => api.getJobAd(id!),
    enabled: !!id,
  });

  if (isLoading || isError || !jobAd) {
    return <DetailPageStatus isLoading={isLoading} message="Job ad not found" back={BACK} />;
  }

  const websiteUrl = ensureHttps(jobAd.website);

  const locationText = [jobAd.city, jobAd.state].filter(Boolean).join(", ");
  const location: React.ReactNode =
    locationText || jobAd.country ? (
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
        {locationText && <span>{locationText}</span>}
        {locationText && jobAd.country && <span>,</span>}
        {jobAd.country && <CountryName code={jobAd.country} />}
      </Box>
    ) : null;

  return (
    <>
      <DetailHeader
        back={BACK}
        title={jobAd.title}
        sourceUrl={ensureHttps(jobAd.url)}
        sourceLabel="View posting"
        chips={
          <>
            <Chip size="small" label={jobAd.source} />
            {jobAd.remote && <Chip size="small" label="Remote" variant="outlined" />}
          </>
        }
      />
      <SectionCard title="Details">
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailRow label="Company" value={jobAd.company_name} />
            <DetailRow label="Location" value={location} />
            <DetailRow label="Remote" value={jobAd.remote != null ? (jobAd.remote ? "Yes" : "No") : null} />
            <DetailRow label="Domain" value={jobAd.domain} />
            <DetailRow
              label="Website"
              value={
                websiteUrl ? (
                  <Link href={websiteUrl} target="_blank" rel="noopener noreferrer" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                    {jobAd.website} <ExternalLink fontSize="inherit" />
                  </Link>
                ) : null
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DetailRow label="External ID" value={jobAd.external_id} />
            <DetailRow label="Published" value={jobAd.published_at ? <FormattedDate value={jobAd.published_at} /> : null} />
            <DetailRow label="Created" value={<FormattedDate value={jobAd.created_at} />} />
            <DetailRow label="Updated" value={<FormattedDate value={jobAd.updated_at} />} />
          </Grid>
        </Grid>
      </SectionCard>
      {jobAd.master_company_id && (
        <MatchedCompanyCard
          companyId={jobAd.master_company_id}
          name={jobAd.company_name}
          method={jobAd.match_method}
          confidence={jobAd.match_confidence}
        />
      )}
      {jobAd.description && (
        <SectionCard title="Description">
          <DescriptionBlock>{jobAd.description}</DescriptionBlock>
        </SectionCard>
      )}
      <SourceDataCard data={jobAd.source_data} />
    </>
  );
}

export default JobAdDetailPage;
